import { Component, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { AsapClientPipe } from 'asap-crud';

export interface CookiePreferences {
  essential: boolean;
  analytics: boolean;
  functional: boolean;
  date?: string;
}

@Component({
  selector: 'app-cookie-consent',
  templateUrl: './cookie-consent.component.html',
  styleUrls: ['./cookie-consent.component.scss']
})
export class CookieConsentComponent implements OnInit {
  public isVisible = false;
  public showPreferencesModal = false;

  // Preferences state
  public essential = true; // Always true & disabled
  public analytics = true;
  public functional = true;

  private translations: Record<string, Record<string, string>> = {
    'pt-br': {
      CookieConsentTitle: 'Privacidade e Gestão de Cookies',
      CookieConsentBadge: 'LGPD Compliance',
      CookieConsentBannerText: 'Utilizamos cookies essenciais para garantir o funcionamento seguro desta plataforma e cookies opcionais para personalizar sua navegação e melhorar sua experiência. De acordo com a <strong>LGPD (Lei nº 13.709/2018)</strong>, você tem o controle sobre seus dados.',
      CookieConsentAcceptAll: 'Aceitar Todos',
      CookieConsentNecessaryOnly: 'Apenas Necessários',
      CookieConsentCustomize: 'Personalizar',
      CookieConsentModalTitle: 'Preferências de Cookies',
      CookieConsentModalSubtitle: 'Gerencie quais cookies você autoriza utilizar em nosso site',
      CookieConsentNecessaryTitle: 'Cookies Estritamente Necessários',
      CookieConsentAlwaysActive: 'Sempre Ativo',
      CookieConsentNecessaryDesc: 'Essenciais para o funcionamento do site, autenticação de login, navegação segura e proteção contra fraudes. Não podem ser desativados.',
      CookieConsentAnalyticsTitle: 'Cookies Analíticos e de Desempenho',
      CookieConsentAnalyticsDesc: 'Ajudam a entender como os visitantes interagem com o sistema, fornecendo informações sobre métricas de acesso, tempo de permanência e identificação de erros.',
      CookieConsentFunctionalTitle: 'Cookies de Funcionalidade',
      CookieConsentFunctionalDesc: 'Permitem que o site lembre de escolhas feitas por você (como seu nome de usuário, idioma ou a região em que você se encontra) e forneça recursos personalizados.',
      CookieConsentRejectOptional: 'Rejeitar Opcionais',
      CookieConsentSavePreferences: 'Salvar Preferências'
    },
    'es': {
      CookieConsentTitle: 'Privacidad y Gestión de Cookies',
      CookieConsentBadge: 'Cumplimiento de Privacidad',
      CookieConsentBannerText: 'Utilizamos cookies esenciales para garantizar el funcionamiento seguro de esta plataforma y cookies opcionales para personalizar su navegación y mejorar su experiencia. De acuerdo con las <strong>normativas de protección de datos</strong>, usted tiene el control sobre sus datos.',
      CookieConsentAcceptAll: 'Aceptar Todos',
      CookieConsentNecessaryOnly: 'Solo Necesarios',
      CookieConsentCustomize: 'Personalizar',
      CookieConsentModalTitle: 'Preferencias de Cookies',
      CookieConsentModalSubtitle: 'Gestione qué cookies autoriza utilizar en nuestro sitio web',
      CookieConsentNecessaryTitle: 'Cookies Estrictamente Necesarias',
      CookieConsentAlwaysActive: 'Siempre Activo',
      CookieConsentNecessaryDesc: 'Esenciales para el funcionamiento del sitio web, autenticación de inicio de sesión, navegación segura y protección contra fraudes. No se pueden desactivar.',
      CookieConsentAnalyticsTitle: 'Cookies Analíticas y de Rendimiento',
      CookieConsentAnalyticsDesc: 'Ayudan a comprender cómo interactúan los visitantes con el sistema, proporcionando información sobre métricas de acceso, tiempo de permanencia e identificación de errores.',
      CookieConsentFunctionalTitle: 'Cookies de Funcionalidad',
      CookieConsentFunctionalDesc: 'Permiten que el sitio web recuerde las elecciones realizadas por usted (como su nombre de usuario, idioma o la región en la que se encuentra) y ofrezca funciones personalizadas.',
      CookieConsentRejectOptional: 'Rechazar Opcionales',
      CookieConsentSavePreferences: 'Guardar Preferencias'
    },
    'en': {
      CookieConsentTitle: 'Privacy and Cookie Management',
      CookieConsentBadge: 'Privacy Compliance',
      CookieConsentBannerText: 'We use essential cookies to ensure the secure operation of this platform and optional cookies to personalize your navigation and enhance your experience. In accordance with <strong>data protection regulations</strong>, you have control over your data.',
      CookieConsentAcceptAll: 'Accept All',
      CookieConsentNecessaryOnly: 'Necessary Only',
      CookieConsentCustomize: 'Customize',
      CookieConsentModalTitle: 'Cookie Preferences',
      CookieConsentModalSubtitle: 'Manage which cookies you authorize to be used on our website',
      CookieConsentNecessaryTitle: 'Strictly Necessary Cookies',
      CookieConsentAlwaysActive: 'Always Active',
      CookieConsentNecessaryDesc: 'Essential for website operation, login authentication, secure browsing, and fraud protection. Cannot be disabled.',
      CookieConsentAnalyticsTitle: 'Analytics and Performance Cookies',
      CookieConsentAnalyticsDesc: 'Help understand how visitors interact with the system by providing insights into access metrics, time spent, and error identification.',
      CookieConsentFunctionalTitle: 'Functional Cookies',
      CookieConsentFunctionalDesc: 'Allow the website to remember choices you make (such as your username, language, or region) and provide personalized features.',
      CookieConsentRejectOptional: 'Reject Optional',
      CookieConsentSavePreferences: 'Save Preferences'
    },
    'fr': {
      CookieConsentTitle: 'Confidentialité et gestion des cookies',
      CookieConsentBadge: 'Conformité de confidentialité',
      CookieConsentBannerText: 'Nous utilisons des cookies essentiels pour assurer le fonctionnement sécurisé de cette plateforme et des cookies facultatifs pour personnaliser votre navigation et améliorer votre expérience. Conformément aux <strong>réglementations sur la protection des données</strong>, vous avez le contrôle de vos données.',
      CookieConsentAcceptAll: 'Tout Accepter',
      CookieConsentNecessaryOnly: 'Nécessaires Uniquement',
      CookieConsentCustomize: 'Personnaliser',
      CookieConsentModalTitle: 'Préférences de Cookies',
      CookieConsentModalSubtitle: 'Gérez les cookies que vous autorisez à utiliser sur notre site',
      CookieConsentNecessaryTitle: 'Cookies Strictement Nécessaires',
      CookieConsentAlwaysActive: 'Toujours Actif',
      CookieConsentNecessaryDesc: "Indispensables au fonctionnement du site, à l'authentification de connexion, à la sécurité et à la prévention des fraudes. Ils ne peuvent pas être désactivés.",
      CookieConsentAnalyticsTitle: "Cookies de Performance et d'Analyse",
      CookieConsentAnalyticsDesc: "Aident à comprendre comment les visiteurs interagissent avec le système en fournissant des informations sur les métriques d'accès, le temps passé et le suivi des erreurs.",
      CookieConsentFunctionalTitle: 'Cookies de Fonctionnalité',
      CookieConsentFunctionalDesc: "Permettent au site de mémoriser les choix que vous faites (tels que votre nom d'utilisateur, votre langue ou votre région) et de fournir des fonctionnalités personnalisées.",
      CookieConsentRejectOptional: 'Rejeter les Facultatifs',
      CookieConsentSavePreferences: 'Enregistrer les Préférences'
    }
  };

  constructor(public translate: TranslateService) {}

  ngOnInit(): void {
    this.syncLanguage();
    this.checkConsent();
  }

  public detectLanguage(): string {
    const clientUrl = (localStorage.getItem('clientURL') || (typeof window !== 'undefined' ? window.location.hostname : '') || '').toLowerCase();

    // Se a URL do cliente for do Marrocos (.ma), o idioma padrão da plataforma é francês
    if (clientUrl.includes('.ma') || clientUrl.includes('maroc')) {
      return 'fr';
    }

    // 1. Idioma definido nas configurações ativas do cliente
    let raw = (AsapClientPipe && AsapClientPipe.dictionary && AsapClientPipe.dictionary.language);

    // 2. Idioma salvo na configuração de cor/tema do cliente
    if (!raw || raw === 'undefined') {
      try {
        const configColor = JSON.parse(localStorage.getItem('config_color') || '{}');
        raw = configColor?.config?.language || configColor?.language;
      } catch (e) {}
    }

    // 3. Idioma no storage
    if (!raw || raw === 'undefined') {
      raw = localStorage.getItem('language') || sessionStorage.getItem('language');
    }

    // 4. Idioma do navegador
    if (!raw || raw === 'undefined') {
      raw = (typeof navigator !== 'undefined' ? (navigator.language || (navigator as any).userLanguage) : null) || 'pt-br';
    }

    const lang = String(raw).toLowerCase().trim();
    if (lang.startsWith('fr')) return 'fr';
    if (lang.startsWith('es')) return 'es';
    if (lang.startsWith('en')) return 'en';
    if (lang.startsWith('pt')) return 'pt-br';
    return 'pt-br';
  }

  public syncLanguage(): void {
    const detectedLang = this.detectLanguage();
    if (detectedLang && this.translate && this.translate.currentLang !== detectedLang) {
      this.translate.use(detectedLang);
    }
  }

  public getText(key: string): string {
    const lang = this.detectLanguage();

    // Se o TranslateService já estiver sincronizado com o idioma detectado, tenta buscar dele
    if (this.translate && this.translate.currentLang === lang) {
      const val = this.translate.instant(key);
      if (val && val !== key) {
        return val;
      }
    }

    // Busca no dicionário interno compilado do idioma detectado
    const dict = this.translations[lang] || this.translations['fr'] || this.translations['pt-br'];
    return (dict && dict[key]) || key;
  }

  public checkConsent(): void {
    const consent = localStorage.getItem('lgpdConsent');
    const legacyAssign = localStorage.getItem('lgpdAssing');

    if (!consent && !legacyAssign) {
      // Delay slightly for smooth entrance after page load
      setTimeout(() => {
        this.syncLanguage();
        this.isVisible = true;
      }, 600);
    } else {
      this.loadSavedPreferences();
    }
  }

  private loadSavedPreferences(): void {
    const saved = localStorage.getItem('cookiePreferences');
    if (saved) {
      try {
        const prefs: CookiePreferences = JSON.parse(saved);
        this.analytics = prefs.analytics ?? true;
        this.functional = prefs.functional ?? true;
      } catch (e) {
        console.error('Error parsing cookie preferences', e);
      }
    }
  }

  public acceptAll(): void {
    this.analytics = true;
    this.functional = true;
    this.saveConsent('accepted', { essential: true, analytics: true, functional: true });
  }

  public acceptNecessary(): void {
    this.analytics = false;
    this.functional = false;
    this.saveConsent('rejected', { essential: true, analytics: false, functional: false });
  }

  public saveCustomPreferences(): void {
    this.saveConsent('custom', { essential: true, analytics: this.analytics, functional: this.functional });
    this.closePreferences();
  }

  private saveConsent(type: 'accepted' | 'rejected' | 'custom', prefs: CookiePreferences): void {
    prefs.date = new Date().toISOString();
    localStorage.setItem('lgpdConsent', type);
    localStorage.setItem('lgpdAssing', 'true');
    localStorage.setItem('cookiePreferences', JSON.stringify(prefs));

    this.isVisible = false;
  }

  public openPreferences(): void {
    this.syncLanguage();
    this.showPreferencesModal = true;
  }

  public closePreferences(): void {
    this.showPreferencesModal = false;
  }
}
